import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ComponentProps } from "react";

type InputFieldProps = ComponentProps<typeof Input> & {
  label?: string;
  error?: string;
  /**
   * Optional `data-testid` forwarded to the underlying <input>. Used by E2E
   * specs that need a stable selector independent of `name`/label changes.
   */
  "data-testid"?: string;
};

const InputField = ({ label, error, ...props }: InputFieldProps) => {
  const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    props.onFocus?.(event);
    // Numeric fields are often prefilled with 0 — select the content on focus
    // so typing replaces it instead of producing "0200" (ticket #628). The
    // rAF survives the browser's own post-focus caret placement on click.
    if (props.type === "number" && !props.readOnly) {
      const input = event.target;
      requestAnimationFrame(() => input.select());
    }
  };

  return (
    <div className={`${props.className} flex w-full flex-col gap-3`}>
      {label && <Label>{label}</Label>}
      <div className="flex w-full flex-col gap-1">
        <Input
          className={`${props.type === "file" ? "cursor-pointer" : ""}`}
          id={props.id}
          name={props.name}
          value={props.value}
          onChange={props.onChange}
          onBlur={props.onBlur}
          onFocus={handleFocus}
          readOnly={props.readOnly}
          hidden={props.hidden}
          type={!props.type ? "text" : props.type}
          placeholder={props.placeholder}
          maxLength={props.maxLength}
          min={props.min}
          max={props.max}
          step={props.step}
          accept={props.accept}
          ref={props.ref}
          aria-invalid={!!error}
          defaultValue={props.defaultValue}
          disabled={props.disabled}
          multiple={props.multiple}
          data-testid={props["data-testid"]}
        />
        {error && (
          <span className="text-destructive pl-1 text-sm">{error}</span>
        )}
      </div>
    </div>
  );
};

export default InputField;
